import { Camera, Gamepad2, RotateCcw, Swords, Upload } from "lucide-react";
import { useState } from "react";
import { useImageCrop } from "../../hooks/useImageCrop";
import { trpc } from "../../lib/trpc";
import { Button, Input, Textarea } from "../ui";
import { ImageCropModal } from "../ui/ImageCropModal";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

const IMAGE_CONFIG = {
  banner: { maxFileSize: 5 * 1024 * 1024, outputWidth: 864, outputHeight: 216, aspect: 4 },
  icon: { maxFileSize: 2 * 1024 * 1024, outputWidth: 256, outputHeight: 256, aspect: 1 },
} as const;

type Props = {
  form: QueueSettingsForm;
  clubId?: string;
  clubGameId?: string;
};

export function GeneralSection({ form, clubId, clubGameId }: Props) {
  const { data: games } = trpc.clubGame.list.useQuery(
    { clubId: clubId ?? "" },
    { enabled: !!clubId },
  );

  const currentGame = games?.find((g) => g.id === clubGameId);
  const { getSrc, handleFileSelect, handleImageClick, cropModalProps } = useImageCrop(
    IMAGE_CONFIG,
    {
      banner: {
        inputRef: form.bannerInputRef,
        preview: form.bannerPreview,
        displayUrl: form.bannerUrl || null,
        sourceUrl: form.bannerSourceUrl || null,
        cropData: form.bannerCropData,
        setPendingSourceFile: form.setPendingBannerSourceFile,
        setPendingBlob: form.setPendingBannerBlob,
        setPreview: form.setBannerPreview,
        setCropData: form.setBannerCropData,
      },
      icon: {
        inputRef: form.iconInputRef,
        preview: form.iconPreview,
        displayUrl: form.iconUrl || null,
        sourceUrl: form.iconSourceUrl || null,
        cropData: form.iconCropData,
        setPendingSourceFile: form.setPendingIconSourceFile,
        setPendingBlob: form.setPendingIconBlob,
        setPreview: form.setIconPreview,
        setCropData: form.setIconCropData,
      },
    },
  );

  const bannerSrc = getSrc("banner");
  const iconSrc = getSrc("icon");
  const [bannerHover, setBannerHover] = useState(false);
  const [iconHover, setIconHover] = useState(false);

  return (
    <div className="space-y-6">
      {/* Game + Name & Description */}
      <div className="space-y-4">
        {currentGame && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-content-secondary">Game</label>
            <div className="flex items-center gap-2">
              {currentGame.iconUrl ? (
                <img
                  src={currentGame.iconUrl}
                  alt=""
                  className="w-5 h-5 rounded object-cover shrink-0"
                />
              ) : (
                <Gamepad2 className="size-4 text-surface-400 shrink-0" />
              )}
              <span className="text-sm text-white font-medium">{currentGame.name}</span>
            </div>
            <p className="text-xs text-surface-500">
              Cannot be changed. To use a different game, create a new queue.
            </p>
          </div>
        )}
        <Input
          label="Queue Name"
          value={form.name}
          onChange={(e) => form.setName(e.target.value)}
          maxLength={100}
        />
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => form.setDescription(e.target.value)}
          maxLength={500}
          rows={2}
        />
        <div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-surface-300">Enabled</label>
            <button
              type="button"
              onClick={() => form.setEnabled((e) => !e)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.enabled ? "bg-brand-500" : "bg-surface-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-surface-500 mt-1">
            When disabled, players cannot join this queue.
          </p>
        </div>
      </div>

      {/* Branding */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-content-secondary">Branding</label>

        {/* Hidden file inputs */}
        <input
          ref={form.bannerInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileSelect("banner")}
          className="hidden"
        />
        <input
          ref={form.iconInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileSelect("icon")}
          className="hidden"
        />

        {/* Queue preview label */}
        <span className="text-2xs font-medium text-surface-500 uppercase tracking-wider block">
          Queue preview
        </span>

        {/* Card — pixel-identical to QueueCard from club page */}
        <div className="relative rounded-xl border border-surface-800/40 overflow-hidden max-w-2xl mx-auto w-full">
          {/* Banner */}
          <div
            className="relative aspect-[4/1] cursor-pointer"
            onMouseEnter={() => setBannerHover(true)}
            onMouseLeave={() => setBannerHover(false)}
            onClick={() => handleImageClick("banner")}
          >
            {bannerSrc ? (
              <img src={bannerSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 overflow-hidden">
                {/* Base gradient */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `
                      radial-gradient(ellipse 70% 200% at 70% 30%, rgba(38,217,181,0.2) 0%, transparent 60%),
                      linear-gradient(135deg, #0d1f1b 0%, #101318 45%, #111a1f 100%)
                    `,
                  }}
                />
                {/* Large angular geometric accent — diamond shard */}
                <div
                  className="absolute top-[-60%] right-[5%] w-[45%] h-[280%] bg-gradient-to-b from-brand-500/[0.12] via-brand-500/[0.06] to-transparent"
                  style={{ transform: "rotate(-20deg)" }}
                />
                {/* Secondary shard — thinner, offset */}
                <div
                  className="absolute top-[-40%] right-[22%] w-[8%] h-[250%] bg-gradient-to-b from-brand-400/[0.1] to-transparent"
                  style={{ transform: "rotate(-20deg)" }}
                />
                {/* Third shard — far left, subtle */}
                <div
                  className="absolute top-[-30%] left-[8%] w-[20%] h-[220%] bg-gradient-to-b from-cyan-400/[0.06] to-transparent"
                  style={{ transform: "rotate(-20deg)" }}
                />
                {/* Bottom-edge glow for grounding */}
                <div className="absolute bottom-0 inset-x-0 h-[40%] bg-gradient-to-t from-brand-500/[0.08] to-transparent" />
                {/* Noise texture */}
                <div className="absolute inset-0 noise-overlay" />
              </div>
            )}
            <div className="absolute inset-0 noise-overlay" />

            {/* Bottom gradient with icon + name */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-10 pb-2.5 px-3">
              <div className="flex items-center gap-2.5">
                {/* Icon — independently hoverable */}
                <div
                  className="relative shrink-0 cursor-pointer"
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    setBannerHover(false);
                    setIconHover(true);
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    setIconHover(false);
                    setBannerHover(true);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick("icon");
                  }}
                >
                  {iconSrc ? (
                    <img
                      src={iconSrc}
                      alt=""
                      className="size-7 rounded-lg object-cover shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
                    />
                  ) : (
                    <div className="size-7 rounded-lg bg-surface-800 border border-surface-700/40 flex items-center justify-center shrink-0">
                      <Swords className="size-3.5 text-surface-500" />
                    </div>
                  )}
                  <div
                    className={`absolute inset-0 bg-surface-950/60 transition-opacity duration-200 flex items-center justify-center rounded-lg ${
                      iconHover ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <Camera className="size-3 text-white" />
                  </div>
                </div>
                <span className="text-base font-display font-bold text-white truncate min-w-0">
                  {form.name || "Queue Name"}
                </span>
              </div>
            </div>

            {/* Banner hover overlay */}
            <div
              className={`absolute inset-0 bg-surface-950/50 transition-opacity duration-200 flex items-center justify-center gap-2 pointer-events-none ${
                bannerHover && !iconHover ? "opacity-100" : "opacity-0"
              }`}
            >
              <Camera className="size-4 text-white" />
              <span className="text-xs text-white/80 font-medium">
                {bannerSrc ? "Adjust banner" : "Upload banner"}
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-2 gap-3 max-w-2xl mx-auto">
          {/* Banner controls */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-900/50 border border-surface-700/30 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-surface-200">Banner</p>
              <p className="text-2xs text-surface-500 whitespace-nowrap">864 × 216, max 5 MB</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {bannerSrc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-0.5 !text-xs"
                  leftIcon={<Camera className="size-3" />}
                  onClick={() => handleImageClick("banner")}
                >
                  Adjust
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="!px-2 !py-0.5 !text-xs"
                leftIcon={
                  bannerSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                }
                onClick={() => form.bannerInputRef.current?.click()}
              >
                {bannerSrc ? "Replace" : "Upload"}
              </Button>
            </div>
          </div>

          {/* Icon controls */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-900/50 border border-surface-700/30 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-surface-200">Icon</p>
              <p className="text-2xs text-surface-500 whitespace-nowrap">256 × 256, max 2 MB</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {iconSrc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-0.5 !text-xs"
                  leftIcon={<Camera className="size-3" />}
                  onClick={() => handleImageClick("icon")}
                >
                  Adjust
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="!px-2 !py-0.5 !text-xs"
                leftIcon={
                  iconSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                }
                onClick={() => form.iconInputRef.current?.click()}
              >
                {iconSrc ? "Replace" : "Upload"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {cropModalProps && <ImageCropModal {...cropModalProps} />}
    </div>
  );
}
